<template>
  <BContainer
    class="px-0"
  >
    <ValidationObserver
      v-if="useForm"
      ref="formProduct"
    >
      <form
        novalidate
        @submit.prevent
      >
        <BRow
          class="mb-2"
        >
          <BCol
            cols="12"
            md="8"
          >
            <ValidationProvider
              v-slot="{ errors }"
              vid="product"
              name="product"
              :rules="state.editing ? 'required' : {
                required: true,
                not_exists: {
                  searchKey: 'id',
                  options: products.map(({ product_id: id }) => id)
                }
              }"
            >
              <BFormGroup
                label="Product"
                label-for="fProduct"
                label-class="font-weight-bolder"
                class="mb-2"
              >
                <VSelect
                  v-model="selected.product"
                  input-id="fProduct"
                  label="product_name"
                  append-to-body
                  placeholder="select product here"
                  :options="notSelectedProducts"
                  :disabled="fetching.products || state.busy || state.editing"
                  :loading="fetching.products"
                  :calculate-position="calculateDropPosition"
                  :class="{ 'is-invalid': errors.length > 0 }"
                  @search="searchProducts"
                >
                  <template #option="{ product_name }">
                    <div
                      class="text-break text-wrap"
                      v-text="product_name"
                    />
                  </template>
                  <template #selected-option="{ product_name }">
                    <div
                      class="w-200px w-lg-300px text-truncate"
                      v-text="product_name"
                    />
                  </template>
                  <template #no-options="">
                    no available products
                  </template>
                </VSelect>
                <div
                  v-if="(errors.length > 0)"
                  class="invalid-feedback"
                  v-text="errors[0]"
                />
              </BFormGroup>
            </ValidationProvider>
          </BCol>

          <BCol
            cols="12"
            md="4"
          >
            <ValidationProvider
              v-slot="{ errors }"
              vid="quantity"
              name="quantity"
              rules="required|min_value:1"
            >
              <BFormGroup
                label="Quantity Sold"
                label-for="fQuantitySold"
                label-class="font-weight-bolder"
                class="mb-2"
              >
                <BFormInput
                  id="fQuantitySold"
                  ref="iQuantitySold"
                  v-model="formProduct.quantity"
                  type="number"
                  step="1"
                  min="1"
                  placeholder="enter quantity here"
                  :state="errors.length > 0 ? false : null"
                  :disabled="state.busy"
                />
                <div
                  v-if="(errors.length > 0)"
                  class="invalid-feedback"
                  v-text="errors[0]"
                />
              </BFormGroup>
            </ValidationProvider>
          </BCol>
          <BCol
            cols="12"
          >
            <div class="d-flex gap-2">
              <BButton
                size="sm"
                variant="primary"
                @click="validateProduct"
              >
                {{ state.editing ? 'Update' : 'Add' }} Product
              </BButton>
              <BButton
                size="sm"
                variant="outline-danger"
                @click.prevent="clearProduct"
              >
                Reset
              </BButton>
            </div>
          </BCol>
        </BRow>
      </form>
    </ValidationObserver>
    <BTable
      ref="tableProducts"
      small
      hover
      bordered
      responsive
      show-empty
      :stacked="tableProducts.stacked"
      :items="products"
      :fields="tableProducts.fields"
    >
      <template #cell(index)="{ index }">
        {{ index + 1 }}
      </template>
      <template #cell(action)="{ item }">
        <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-center gap-2">
          <BButton
            size="sm"
            variant="primary"
            @click.prevent="editProduct(item)"
          >
            Edit
          </BButton>
          <BButton
            size="sm"
            variant="outline-danger"
            @click.prevent="deleteProduct(item)"
          >
            Delete
          </BButton>
        </div>
      </template>
    </BTable>
  </BContainer>
</template>
<script>
import QUERY from 'lodash'

import MxMisc from '@/mixins/misc'
import MProducts from '@/mixins/maintenances/products'

export default {
  name: 'FSOTaskEventsProducts',

  mixins: [
    MxMisc,
    MProducts
  ],

  props: {
    useForm: {
      type: Boolean,
      default: true
    },
    products: {
      type: Array,
      required: true,
      default: () => []
    }
  },

  emits: ['updated'],

  data () {
    return {
      state: {
        busy: false,
        editing: false
      },
      formProduct: {
        product_id: '',
        product_name: '',
        quantity: ''
      },
      tableProducts: {
        busy: false,
        stacked: 'sm',
        rows: this.products,
        fields: this.useForm ? [
          { mobile: 1, key: 'index', label: '#' },
          { mobile: 5, key: 'action', class: 'text-center w-md-125px' },
          { mobile: 2, key: 'product_name', label: 'Product Name' },
          { mobile: 3, key: 'quantity', label: 'Quantity Sold' }
        ].sort(this.scaleOnMobile) : [
          { mobile: 1, key: 'index', label: '#' },
          { mobile: 2, key: 'product_name', label: 'Product Name' },
          { mobile: 3, key: 'quantity', label: 'Quantity Sold' }
        ]
      }
    }
  },

  computed: {
    notSelectedProducts () {
      const products = Array.from(this.tableProducts.rows).map(
        product => product.product_id
      )

      return this.options.products.filter(
        ({ id }) => !products.includes(id)
      )
    }
  },

  watch: {
    'selected.product' (product) {
      this.formProduct.product_id = product?.id || ''
      this.formProduct.product_name = product?.product_name || ''

      if (product?.id) {
        this.$refs.iQuantitySold.focus()
      }
    }
  },

  mounted () {
    this.getProducts(this)
  },

  methods: {

    clearProduct () {
      this.state.editing = false

      this.selected.product = ''

      this.formProduct = {
        product_id: '',
        product_name: '',
        quantity: ''
      }

      this.$refs.formProduct.reset()
    },

    editProduct (product) {
      this.state.editing = true

      this.formProduct = Object.assign(this.formProduct, {
        quantity: product.quantity
      })

      this.selected.product = QUERY.find(this.options.products, {
        id: product.product_id
      }) || ''
    },

    validateProduct () {
      this.$refs.formProduct.validate().then(
        async validProduct => {
          if (!validProduct) {
            return this.swalInvalid()
          }

          const productExists = QUERY.find(this.tableProducts.rows, {
            product_id: this.formProduct.product_id
          })

          const createOrUpdate = async () => {
            return this.state.editing ? await this.putProduct(productExists) : await this.postProduct(productExists)
          }

          return createOrUpdate().finally(() => {
            this.clearProduct()
            this.refetch()
          })
        }
      )
    },

    postProduct (productExists) {
      if (productExists) {
        return this.$refs.formProduct.setErrors({
          product: 'The selected product already exists.'
        })
      }

      this.tableProducts.rows.push(this.formProduct)
    },

    putProduct (productExists) {
      if (!productExists) {
        return this.$refs.formProduct.setErrors({
          product: 'The selected product does not exist.'
        })
      }

      productExists.product_name = this.formProduct.product_name
      productExists.quantity = this.formProduct.quantity
    },

    deleteProduct (product) {
      this.tableProducts.rows = QUERY.filter(this.tableProducts.rows,
        ({ product_id: id }) => Number(id) !== Number(product.product_id)
      )

      this.refetch()
    },

    refetch () {
      this.$emit('updated', this.tableProducts.rows)
      this.$refs.tableProducts.refresh()
    }
  }
}
</script>
